import React, { useState } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, CircularProgress, Alert, Collapse, Card, CardContent, Grid, Chip, Tooltip, IconButton, List, ListItem, ListItemText } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ApiService from '../../api/api_service';

const RequestTable = ({
    requests = [],
    loading = false,
    error = null,
    onViewOnMap,
    onDelete, 
    onReplicate,
}) => {
    const [expandedRow, setExpandedRow] = useState(null); 
    const [downloadingId, setDownloadingId] = useState(null);

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case "DONE":
                return "success";
            case "PROCESSING":
                return "warning";
            case "CANCELLED":
            case "FAILED":
                return "error";
            case "PENDING":
                return "info";
            default:
                return "default";
        }
    };

    const getResultModelTypes = (results) => {
        if (!results || results.length === 0) {
            return 'N/A';
        }
        const modelTypes = Array.from(new Set(results.map(result => result.model_type).filter(Boolean)));
        
        if (modelTypes.length === 0) {
            return 'N/A';
        }

        return modelTypes.map((model, index) => <div key={index}>{model.toUpperCase()}</div>);
    };

    const formatDateTime = (value) => {
        if (!value) return "N/A";
        try {
            return new Date(value).toLocaleString();
        } catch (e) {
            return value;
        }
    };

    const renderThresholdDetails = (thresholdConfig) => {
        if (!thresholdConfig) return "N/A";
        if (thresholdConfig.mode === 'range' || (thresholdConfig.ranges && thresholdConfig.ranges.length > 0)) {
            return thresholdConfig.ranges
                ?.map((range, idx) => {
                    const minLabel = range.min === null || range.min === undefined ? '-∞' : range.min;
                    const maxLabel = range.max === null || range.max === undefined ? '∞' : range.max;
                    return `${minLabel} to ${maxLabel}: ${range.score ?? 0}`;
                })
                .join('; ');
        }
        if (Array.isArray(thresholdConfig.thresholds)) {
            return thresholdConfig.thresholds.join(', ');
        }
        return "N/A";
    };

    const renderFilterDetails = (thresholdConfig) => {
        if (!thresholdConfig) return "—";
        const constraintType = (thresholdConfig.constraint_type || 'distance').toLowerCase();
        const filterValues = thresholdConfig.filter?.values;
        if (Array.isArray(filterValues) && filterValues.length > 0) {
            return filterValues.join(', ');
        }
        return constraintType === 'include' ? 'Membership' : '—';
    };

    const renderGeometryProperties = (properties) => {
        if (!properties || Object.keys(properties).length === 0) {
            return "N/A";
        }
        return (
            <List dense disablePadding>
                {Object.entries(properties).map(([key, value]) => (
                    <ListItem key={key} disablePadding sx={{ py: 0.25 }}>
                        <ListItemText
                            primaryTypographyProps={{ variant: "body2" }}
                            primary={
                                <>
                                    <strong>{key}:</strong> {String(value)}
                                </>
                            }
                        />
                    </ListItem>
                ))}
            </List>
        );
    };

    const parseArgs = (args) => {
      try {
        const parsedOuterArgs = JSON.parse(args); 
        if (parsedOuterArgs?.args?.[0]) {
          const parsedInnerArgs = JSON.parse(parsedOuterArgs.args[0]); 
          return parsedInnerArgs;
        }
        return null;
      } catch (error) {
        console.error("Error parsing args:", error);
        return null;
      }
    };

    const hasRenderableGeometry = (result) => {
        if (!result) return false;
        const hasRawGeometry = result.results && typeof result.results === 'object' && Object.keys(result.results).length > 0;
        const hasDetailedFeatures = Array.isArray(result.results_geojson?.features) && result.results_geojson.features.length > 0;
        return hasRawGeometry || hasDetailedFeatures;
    };

    const countVertices = (geometry) => {
        if (!geometry || !geometry.coordinates) return null;
        const recurse = (coords) => {
            if (!Array.isArray(coords)) return 0;
            if (typeof coords[0] === "number") return 1;
            return coords.reduce((sum, entry) => sum + recurse(entry), 0);
        };
        return recurse(geometry.coordinates);
    };

    const handleViewResult = (result, request) => {
        if (hasRenderableGeometry(result)) {
            const payload = {
                result,
                originalGeometry: request.request_geometry?.geometry
            };
            // Pass a payload with both the result and the original geometry
            onViewOnMap(payload);
        } else {
            console.warn("No valid geometry data found for this result.");
        }
    };

    const handleViewGeometry = (requestId, geometry = null) => {
        const combinedFeatures = [];

        const request = requests.find(req => req.id === requestId);

        if (geometry && geometry.type && geometry.coordinates) {
            combinedFeatures.push({
                type: "Feature",
                geometry: geometry,
                properties: {
                    __layerType: 'original'
                }
            });
        }

        if (request && request.request_result && request.request_result.length > 0) {
            request.request_result.forEach((result) => {
                if (Array.isArray(result.results_geojson?.features) && result.results_geojson.features.length > 0) {
                    result.results_geojson.features.forEach((feature) => {
                        combinedFeatures.push({
                            type: "Feature",
                            geometry: feature.geometry,
                            properties: {
                                ...(feature.properties || {}),
                                model_type: result.model_type,
                                __modelType: result.model_type?.toUpperCase() || result.model_type,
                                __layerType: feature.properties?.__layerType || 'result'
                            }
                        });
                    });
                } else if (result.results && typeof result.results === 'object' && Object.keys(result.results).length > 0) {
                    combinedFeatures.push({
                        type: "Feature",
                        geometry: result.results,
                        properties: { 
                            model_type: result.model_type,
                            __modelType: result.model_type?.toUpperCase() || result.model_type,
                            __layerType: 'result'
                        }
                    });
                }
            });
        }

        if (combinedFeatures.length > 0) {
            const combinedLayer = {
                type: "FeatureCollection",
                features: combinedFeatures,
            };
            onViewOnMap(combinedLayer);
        } else {
            console.warn("No valid geometries or results found to render on the map.");
        }
    };

    const handleRowClick = (requestId) => {
      setExpandedRow(expandedRow === requestId ? null : requestId); 
    };

    const handleDownloadReport = async (event, requestId) => {
        event.stopPropagation();
        try {
            setDownloadingId(requestId);
            const { blob, filename } = await ApiService.downloadReport(requestId);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename || `MSP4GROWTH_Report_${requestId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to download report:", error);
            alert(error.message || "Unable to download report.");
        } finally {
            setDownloadingId(null);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ marginTop: 2, padding: 2 }}>
                <Alert severity="error">Error fetching data: {error}</Alert>
            </Box>
        );
    }

    const noRequestsMessage = `No requests found.`;

    return (
        <Box sx={{ width: "100%", maxWidth: "1200px", margin: "0 auto" }}>
            {requests.length === 0 ? (
                <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', marginTop: 2 }}>
                    {noRequestsMessage}
                </Typography>
            ) : (
                <TableContainer component={Paper} sx={{ width: "100%" }}>
                    <Table stickyHeader aria-label="user requests table">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Model</TableCell>
                                {/* Added missing header for Action column */}
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* Map over the requests prop */}
                            {requests.map((request) => {
                                const hasResultsWithGeometry = request.request_result?.some(res => hasRenderableGeometry(res));
                                const canViewCombinedGeometry = hasResultsWithGeometry || Boolean(request.request_geometry?.geometry);

                                const parsedArgs = parseArgs(request.args);
                                const canDownloadReport = request.report_available;
                                const geometryDetails = request.request_geometry;
                                const vertexCount = countVertices(geometryDetails?.geometry);

                                return (
                                  <React.Fragment key={request.id}>
                                    <TableRow
                                      hover
                                      onClick={() => handleRowClick(request.id)} // Handle row click
                                      sx={{ cursor: "pointer" }}
                                    >
                                      <TableCell>{request.id}</TableCell>
                                      <TableCell>
                                          {/* Wrap Chip with Tooltip */}
                                          <Tooltip title={request.status?.toUpperCase() === "FAILED" && request.failure_message ? request.failure_message : undefined}>
                                            <Chip
                                                label={request.status || "N/A"}
                                                color={getStatusColor(request.status)}
                                                size="small"
                                            />
                                          </Tooltip>
                                      
                                      </TableCell>
                                      <TableCell>
                                        {getResultModelTypes(request.request_result)}
                                      </TableCell>
                                      <TableCell sx={{ textAlign: "center" }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                color="primary"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewGeometry(request.id, request.request_geometry?.geometry);
                                                }}
                                                disabled={!canViewCombinedGeometry}
                                                sx={{ textTransform: "none", width: '100%' }}
                                            >
                                                View All
                                            </Button>
                                            {request.request_result && request.request_result.map((result) => {
                                                const hasGeom = hasRenderableGeometry(result);
                                                return (
                                                    <Button
                                                        key={result.model_type}
                                                        variant="outlined"
                                                        size="small"
                                                        color="primary"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleViewResult(result, request);
                                                        }}
                                                        disabled={!hasGeom}
                                                        sx={{ textTransform: "none", width: '100%' }}
                                                    >
                                                        View {result.model_type?.toUpperCase() || ''}
                                                    </Button>
                                                );
                                            })}
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                color="secondary"
                                                onClick={(e) => handleDownloadReport(e, request.id)}
                                                disabled={!canDownloadReport || downloadingId === request.id}
                                                sx={{ textTransform: "none", width: '100%' }}
                                            >
                                                {downloadingId === request.id ? "Downloading..." : "PDF Report"}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                color="info"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (typeof onReplicate === 'function' && parsedArgs) {
                                                        onReplicate({
                                                            request,
                                                            args: parsedArgs,
                                                            geometry: request.request_geometry
                                                        });
                                                    } else {
                                                        console.warn("Missing data; unable to replicate request.");
                                                    }
                                                }}
                                                disabled={typeof onReplicate !== 'function' || !parsedArgs}
                                                sx={{ textTransform: "none", width: '100%' }}
                                            >
                                                Replicate
                                            </Button>
                                            {typeof onDelete === 'function' && (
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    title="Delete"
                                                    sx={{ mt: 0.5 }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        try {
                                                            onDelete(request);
                                                        } catch (err) {
                                                            console.error('onDelete handler error', err);
                                                        }
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell
                                        style={{ paddingBottom: 0, paddingTop: 0 }}
                                        colSpan={4}
                                      >
                                        <Collapse
                                          in={expandedRow === request.id}
                                          timeout="auto"
                                          unmountOnExit
                                        >
                                          <Card sx={{ marginRight:0, marginTop:2 }}>
                                            <CardContent>
                                              <Typography variant="h6" gutterBottom>
                                                Request Details
                                              </Typography>
                                              <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                  <Typography variant="body2">
                                                    <strong>Use Case:</strong>{" "}
                                                    {parsedArgs?.use_case || "N/A"}
                                                  </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                  <Typography variant="body2">
                                                    <strong>Area of Interest:</strong>{" "}
                                                    {geometryDetails?.name || parsedArgs?.areaOfInterest || "N/A"}
                                                  </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                  <Typography variant="body2">
                                                    <strong>Geometry ID:</strong>{" "}
                                                    {geometryDetails?.geom_id ?? parsedArgs?.areaOfInterest_index ?? "N/A"}
                                                  </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                  <Typography variant="body2">
                                                    <strong>Selected On:</strong>{" "}
                                                    {formatDateTime(geometryDetails?.created_on)}
                                                  </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                  <Typography variant="body2">
                                                    <strong>Vertices:</strong>{" "}
                                                    {vertexCount ?? "N/A"}
                                                  </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                  <Typography variant="body2" gutterBottom>
                                                    <strong>Thresholds and Weights:</strong>
                                                  </Typography>
                                                  {parsedArgs?.thresholds ? (
                                                    <TableContainer
                                                      component={Paper}
                                                      sx={{ maxWidth: "100%" }}
                                                    >
                                                      <Table
                                                        size="small"
                                                        aria-label="thresholds table"
                                                      >
                                                        <TableHead>
                                                          <TableRow>
                                                            <TableCell sx={{ fontWeight: "bold" }}>
                                                              Parameter
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: "bold" }}>
                                                              Thresholds
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: "bold" }}>
                                                              Class / Filter
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: "bold" }}>
                                                              Weight
                                                            </TableCell>
                                                          </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                          {Object.entries(
                                                            parsedArgs.thresholds
                                                          ).map(([key, value]) => (
                                                            <TableRow key={key}>
                                                              <TableCell>{key}</TableCell>
                                                              <TableCell>
                                                                {renderThresholdDetails(value)}
                                                              </TableCell>
                                                              <TableCell>
                                                                {renderFilterDetails(value)}
                                                              </TableCell>
                                                              <TableCell>
                                                                {value.weights || "N/A"}
                                                              </TableCell>
                                                            </TableRow>
                                                          ))}
                                                        </TableBody>
                                                      </Table>
                                                    </TableContainer>
                                                  ) : (
                                                    <Typography variant="body2">
                                                      No thresholds available
                                                    </Typography>
                                                  )}
                                                </Grid>
                                                <Grid item xs={12}>
                                                  <Typography variant="body2" gutterBottom>
                                                    <strong>Geometry Properties:</strong>
                                                  </Typography>
                                                  {geometryDetails?.properties
                                                    ? renderGeometryProperties(geometryDetails.properties)
                                                    : "N/A"}
                                                </Grid>
                                              </Grid>
                                            </CardContent>
                                          </Card>
                                        </Collapse>
                                      </TableCell>
                                    </TableRow>
                                  </React.Fragment>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  );
                };
                
export default RequestTable;
